import { yupResolver } from "@hookform/resolvers/yup";
import Picker from "emoji-picker-react";
import { get, size } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import allImgPaths from "../../assets";
import { Checkbox, DropDownName, TextBox } from "../../components";
import Button from "../../components/common/Button";
import Layout, { Breadcrumb, Container } from "../../components/layout";
import { useAppState } from "../../context";
import useOutsideClick from "../../hooks/useOutsideClick";
import { generateUniqId } from "../../shared/functions";
import { createQuestionariesSchema } from "../../validations";

const QuestionariesCreateEdit = ({ type }: { type: string }) => {
  const navigate = useNavigate();
  const {
    createQuestionaries,
    curQuestionariesEditData,
    editQuestionaries,
    updateQuestionaries,
  } = useAppState("questionaries");
  const [step] = useState(type);
  const params = useParams();
  const [currentSlug, setCurrentSlug] = useState<string>("");
  const [curIndex, setCurIndex] = useState<number>(0);
  const [showPickerIndex, setShowPickerIndex] = useState<number | null>(null);
  const actionsRef = useRef(null);

  const {
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
    control,
  } = useForm({
    defaultValues: {
      title: "",
      subTitle: "",
      sequence: "",
      slug: "",
      skill: 0,
      validationCount: 0,
      tags: [
        {
          sequence: "",
          name: "",
          id: generateUniqId(),
          chosenEmoji: null,
        },
      ],
    },
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(
      createQuestionariesSchema(step === "2" ? true : false),
    ),
  });

  const {
    fields,
    append,
    update,
    remove: removeParameters,
  } = useFieldArray({
    control,
    name: "tags",
  });

  // Function to check for unique sequence values in tags
  const uniqueSequenceTest = (tags: any) => {
    const sequences = tags.map((tag: any) => tag.sequence);
    return new Set(sequences).size === sequences.length;
  };

  // Submit handler
  const onSubmit = async (data: any) => {
    // Validate unique sequence values in tags
    if (!uniqueSequenceTest(data.tags)) {
      toast("Duplicate sequence values are not allowed.");
      return; // Prevent form submission
    }

    // Proceed with form submission based on step
    if (step === "1") {
      if (await createQuestionaries(data)) {
        setTimeout(() => {
          navigate("/questionaries");
        }, 1000);
      }
    }

    if (step === "2") {
      if (await updateQuestionaries({ ...params, ...data })) {
        navigate("/questionaries");
      }
    }
  };

  // Fetch and set values for editing
  useEffect(() => {
    if (step === "2") {
      editQuestionaries(params.id);
    }
  }, [step]);

  // Set form values when editing
  useEffect(() => {
    if (step === "2" && size(curQuestionariesEditData)) {
      setValue("title", get(curQuestionariesEditData, "title", ""));
      setValue("subTitle", get(curQuestionariesEditData, "subTitle", ""));
      setValue("sequence", get(curQuestionariesEditData, "sequence", ""));
      setValue("slug", get(curQuestionariesEditData, "slug", ""));
      setValue("skill", get(curQuestionariesEditData, "skill", 0));
      setValue(
        "validationCount",
        get(curQuestionariesEditData, "validationCount", 0),
      );
      setValue("tags", get(curQuestionariesEditData, "tags", [])); // Ensure tags are correctly set
    }
  }, [curQuestionariesEditData]);
  // Watch for changes in the 'slug' field
  const slugValue = watch("slug");

  useEffect(() => {
    setCurrentSlug(slugValue);
  }, [slugValue]);
  const questionSlug: any = [
    { name: "Hours", id: 1 },
    { name: "Interest", id: 2 },
    { name: "Skills & Experience", id: 3 },
    { name: "Feeling", id: 4 },
    { name: "Motivators", id: 5 },
    { name: "Skills", id: 6 },
    { name: "Career Level", id: 7 },
    { name: "Role", id: 8 },
  ];

  const onEmojiClickHandler = (event: any, emojiObject: any, index: any) => {
    const curObj = {
      ...fields[curIndex],
      name: getValues(`tags`)[index]?.name,
      sequence: getValues(`tags`)[index]?.sequence,
      chosenEmoji: emojiObject?.target?.currentSrc,
    };
    update(curIndex, curObj);
    setShowPickerIndex(null); // Hide the picker after emoji is selected
  };
  useOutsideClick(actionsRef, (): void => {
    setShowPickerIndex(null);
  });
  return (
    <Layout>
      <Breadcrumb />
      <Container>
        <div className="">
          <h1 className="font-semibold text-xl">
            {step === "1" ? "Add Questionaries" : "Edit Questionaries"}
          </h1>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="">
                  <label htmlFor="" className="font-medium">
                    Main Title
                  </label>
                  <div className="">
                    <TextBox
                      label="Enter Main Title "
                      name="title"
                      type="text"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
                <div className="">
                  <label htmlFor="" className="font-medium">
                    Sub Title <span className="text-[red]">*</span>
                  </label>
                  <div className="">
                    <TextBox
                      label="Enter Sub Title "
                      name="subTitle"
                      type="text"
                      className="truncate"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="">
                  <label htmlFor="" className="font-medium">
                    Sequence <span className="text-[red]">*</span>
                  </label>
                  <div className="">
                    <TextBox
                      label="Enter Sequence Number "
                      name="sequence"
                      type="number"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
                <div className="">
                  <label htmlFor="" className="font-medium">
                    Max Count <span className="text-[red]">*</span>
                  </label>
                  <div className="">
                    <TextBox
                      label="Max Count*"
                      name="validationCount"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>

              <div className="my-6 flex items-center">
                <label htmlFor="" className="font-medium mr-2">
                  Skills
                </label>
                <Checkbox
                  label=""
                  name="skill"
                  control={control}
                  errors={errors}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="">
                  <label htmlFor="" className="font-medium mr-2">
                    Type <span className="text-[red]">*</span>
                  </label>
                  <div className="">
                    <DropDownName
                      trigger="Type"
                      isRequired={true}
                      options={questionSlug}
                      name="slug"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
              <div ref={actionsRef}>
                {showPickerIndex !== null && currentSlug === "Feeling" && (
                  <Picker
                    reactionsDefaultOpen={true}
                    onEmojiClick={(event, emojiObject) =>
                      onEmojiClickHandler(event, emojiObject, showPickerIndex)
                    }
                    style={{
                      width: "330px",
                      height: "300px",
                      marginTop: "10px",
                    }}
                  />
                )}
              </div>
              <div className="my-3">
                <label htmlFor="" className="font-medium">
                  Tags <span className="text-[red]">*</span>
                </label>
                <div>
                  {/* add options here from tags */}
                  {fields.map((input, index) => (
                    <div
                      // className="grid grid-cols-6 gap-4 items-start"
                      className={`grid ${currentSlug === "Feeling" ? "grid-cols-12" : " grid-cols-6"} gap-3 items-start`}
                      key={input.id}
                    >
                      <div className="col-span-2">
                        <TextBox
                          label="Name"
                          name={`tags[${index}].name`}
                          control={control}
                          errors={errors}
                        />
                      </div>
                      <div className="col-span-2">
                        <TextBox
                          type="number"
                          label="Sequence"
                          name={`tags[${index}].sequence`}
                          control={control}
                          errors={errors}
                        />
                      </div>
                      {currentSlug === "Feeling" && (
                        <div className="col-span-2 flex gap-4 mt-3.5">
                          <Button
                            variant="outlined"
                            className="w-10 !h-10 !p-2.5"
                            onClick={() => {
                              setCurIndex(index);
                              setShowPickerIndex(
                                showPickerIndex === index ? null : index,
                              );
                            }}
                          >
                            {showPickerIndex === index
                              ? "Close Picker"
                              : "Open Picker"}
                          </Button>
                          {input?.chosenEmoji && (
                            <img
                              src={input?.chosenEmoji}
                              className="w-10 h-10"
                            />
                          )}
                        </div>
                      )}

                      <div className="flex items-center mt-3.5">
                        <div>
                          <button
                            type="button"
                            onClick={() => removeParameters(index)}
                            className="bg-[#D85564] rounded-md h-9"
                          >
                            <img src={allImgPaths.actionTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-start mt-5 gap-7">
                    <div>
                      <Button
                        variant="contained"
                        className="!h-10 !px-4 !text-sm"
                        onClick={() =>
                          append({
                            sequence: "",
                            name: "",
                            id: generateUniqId(),
                            chosenEmoji: null,
                          })
                        }
                      >
                        Add More..
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center mt-20 gap-5">
                <div>
                  <Button
                    variant="outlined"
                    className="btn-color !h-10 !text-sm !px-4"
                    onClick={() => {
                      navigate("/questionaries");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
                <div>
                  <Button
                    variant="contained"
                    className="!h-10 !text-sm"
                    type="submit"
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </Container>
    </Layout>
  );
};

export default QuestionariesCreateEdit;
